import React, { useContext, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import imageConverter from '../../../../helpers/imageConverter'
import QRCode from 'qrcode'
import { pdf } from '@react-pdf/renderer'

// MUI Imports
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import API from '../../../../services/API'
import CompliancePage from './PDF/CompliancePage'
import BigModal from '../../../../components/Modals/BigModal'
import IDCheckQRCode from './IDCheckQRCode'
import {
  GlobalContext,
  TenantSettingsContext,
  UserContext,
} from '../../../../App'
import moment from 'moment'
import UpgradeRequired from '../../../../components/Upgrade/UpgradeGate'
import { Stack, Typography } from '@mui/material'
import { Lock } from '@mui/icons-material'
import UpgradeLock from '../../../../assets/icons/UpgradeLock'

const Actions = ({ employeeID, setEmployee, setEditEmployee, employee }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [showIDCheckURL, setShowIDCheckURL] = useState(false)
  const [generatePDF, setGeneratePDF] = useState(false)
  const handleClick = (e) => setAnchorEl(e.currentTarget)
  const open = !!anchorEl
  const navigate = useNavigate()

  const fileInput = useRef()

  const handlePhotoChange = async (e) => {
    try {
      const file = e.target.files[0]
      const image = await imageConverter(file)
      API.put('/employee/photo', {
        employeeID,
        image,
      }).then((res) => {
        if (res.status === 200) {
          setAnchorEl(null)
          setEmployee((prev) => ({
            ...prev,
            photograph: image,
          }))
        }
      })
    } catch (e) {
      console.error(e)
    }
  }

  const { user } = useContext(UserContext)

  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const { tenantInfo, setUpgradeRequired, upgradeRequired } =
    useContext(GlobalContext)

  const [downloadingCompliance, setDownloadingCompliance] = useState(false)

  const downloadCompliance = async () => {
    try {
      setDownloadingCompliance(true)

      const idCheckURL = `${window.location.origin}/id-check/${employee.employee._id}`
      const idCheckQR = await QRCode.toDataURL(idCheckURL)

      // you already have orgSettings/user in your other component;
      // pull them here if needed via contexts, or pass them down as props
      // (example assumes you can access them here)
      const doc = (
        <CompliancePage
          employee={employee}
          orgSettings={orgSettings}
          user={user}
          idCheckQR={idCheckQR}
          idCheckURL={idCheckURL}
        />
      )

      const blob = await pdf(doc).toBlob()
      const url = URL.createObjectURL(blob)

      const fileName = `${employee.firstName}_${
        employee.lastName
      }_COMPLIANCE_${moment().format('YYYYMMDD')}.pdf`

      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      a.remove()

      URL.revokeObjectURL(url)

      setAnchorEl(null) // close menu safely AFTER download trigger
    } catch (e) {
      console.error(e)
    } finally {
      setDownloadingCompliance(false)
    }
  }

  return (
    <Box width="128px">
      <Button fullWidth onClick={handleClick} endIcon={<ArrowDropDownIcon />}>
        Actions
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open && !upgradeRequired}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => fileInput.current.click()}>
          Edit Photo
        </MenuItem>
        <input
          ref={fileInput}
          type="file"
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handlePhotoChange}
        />
        <MenuItem onClick={() => setEditEmployee(true)}>Edit Employee</MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null)

            if (Number(tenantInfo?.subscriptionTier) < 3) {
              setTimeout(() => {
                setUpgradeRequired({
                  requiredTier: 3,
                  currentTier: tenantInfo.subscriptionTier,
                  title: 'Public ID Checking',
                  benefits: [
                    "Publically verify an employee's identification",
                    'Create Public URLs',
                    'Render QR Codes',
                  ],
                })
              }, 0)
            } else {
              setTimeout(() => {
                setShowIDCheckURL(true)
              }, 0)
            }
          }}
        >
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Typography>Show ID Check URL</Typography>
            {tenantInfo?.subscriptionTier < 3 && <UpgradeLock />}
          </Stack>
        </MenuItem>
        {employee.employeeApplication && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null)
              if (Number(tenantInfo?.subscriptionTier < 2)) {
                setTimeout(() => {
                  setUpgradeRequired({
                    requiredTier: 2,
                    currentTier: tenantInfo.subscriptionTier,
                    title: 'Employee Applications',
                    benefits: [
                      'Allow Prospective Employees to Apply Online',
                      'Receive References Electronically',
                      'Document Interviews',
                    ],
                  })
                }, 0)
              } else {
                setTimeout(() => {
                  navigate(
                    '/employees/applications/' + employee.employeeApplication,
                  )
                }, 0)
              }
            }}
            disabled={!employee?.employeeApplication}
          >
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Typography>View Application</Typography>
              {tenantInfo?.subscriptionTier < 2 && <UpgradeLock />}
            </Stack>
          </MenuItem>
        )}
        <MenuItem disabled={downloadingCompliance} onClick={downloadCompliance}>
          {downloadingCompliance
            ? 'Generating compliance PDF…'
            : 'Download Compliance'}
        </MenuItem>
      </Menu>
      <BigModal
        autoWidth
        open={showIDCheckURL && (tenantInfo?.subscriptionTier || 0) > 1}
        onClose={() => setShowIDCheckURL(false)}
      >
        <IDCheckQRCode employeeID={employee.employee._id} />
      </BigModal>
    </Box>
  )
}

export default Actions
